import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { Typography } from '../../../design-system/components/typography/Typography';
import { Destination } from '../../../resources/destination/domain/types/destination';
import { Region } from '../../../resources/region/domain/types/region';
import { useUrlGenerator } from '../../hooks/useUrlGenerator';
import { IntrinsicElement } from '../../utilities/types/IntrinsicElement';
import { PickDomain } from '../../utilities/types/PickDomain';

import style from './SecondaryRegionListSection.module.css';

type Props = {
    destination: PickDomain<Destination, 'name' | 'preposition'>;
    titleElement?: IntrinsicElement;
    regionNameElement?: IntrinsicElement;
    regions: Array<
        PickDomain<
            Region,
            'id' | 'name' | 'slug',
            {
                destination: PickDomain<Region['destination'], 'id' | 'slug'>;
            }
        >
    >;
};

export function SecondaryRegionListSection({
    destination,
    regions,
    titleElement = 'div',
    regionNameElement = 'span',
}: Props) {
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();

    return (
        <div className={style.container}>
            <Typography as={titleElement} scale="primary-s-extra-bold" className={style.title}>
                {formatMessage(
                    { id: 'global.region.cloudListing' },
                    {
                        destinationPreposition: destination.preposition,
                        destinationName: destination.name,
                    },
                )}
            </Typography>

            <div className={style.regionsWrapper}>
                {regions.map((region, index) => {
                    return (
                        <a
                            key={region.id}
                            className={style.secondaryRegion}
                            href={generatePath('region', {
                                destinationSlug: region.destination.slug,
                                regionSlug: region.slug,
                            })}
                            onClick={() => {
                                trackEvent((ampli, defaultProperties) => {
                                    ampli.regionClicked({
                                        ...defaultProperties,
                                        block: 'where_to_go',
                                        click_type: 'button',
                                        card_number: index + 1,
                                        region_id: region.id,
                                        region_slug: region.slug,
                                        destination_id: region.destination.id,
                                        destination_slug: region.destination.slug,
                                    });
                                });
                            }}
                        >
                            <Typography as={regionNameElement} scale="primary-xs-bold">
                                {region.name}
                            </Typography>
                        </a>
                    );
                })}
            </div>
        </div>
    );
}
